import { useEffect, type ReactNode } from 'react';

/**
 * Pins the app to light mode, unconditionally.
 *
 * This product surface is drawn entirely in the agency's own fixed palette
 * (`agency-*` tokens, hardcoded hex values matching the Figma reference) —
 * every screen already renders identically regardless of theme except where
 * a component strayed onto the base shadcn tree (`bg-card`, `text-foreground`,
 * etc.), which genuinely does invert under `.dark`. Rather than rely on every
 * such component being caught and fixed one at a time, the theme is not
 * read from anywhere — not a stored preference, not `prefers-color-scheme` —
 * and `.dark` is never applied, so nothing in the app can render dark no
 * matter what the OS, browser, or a stale `localStorage` value says.
 *
 * There is no user-facing theme control — the `ui` slice previously held a
 * `theme: 'system'` preference with a toggle that was never mounted by any
 * route, which is exactly what caused pages to silently go dark on a machine
 * whose OS preference is dark, with nothing visible to undo it. Both were
 * removed rather than left as dead, disconnected plumbing.
 */
export function ThemeProvider({ children }: { children: ReactNode }) {
  useEffect(() => {
    const root = document.documentElement;
    root.classList.remove('dark');
    root.style.colorScheme = 'light';
  }, []);

  return <>{children}</>;
}
